import { DataTableSearch } from '@/common/ui/table/data-table-search'
import { DataTableProcessorSelector } from '@/common/ui/table/data-table-processor-selector'
import { DataTableStatusSelector } from '@/common/ui/table/data-table-status-selector'
import { WithdrawTransactionResponse } from '../../types'
import {
	processorOptions,
	statusOptions,
	defaultStatusValues,
	columns,
	playerBankTypeOptions,
} from './constants'
import { Button } from '@/common/ui/button'
import { Icons } from '@/common/ui/icons'
import { RefetchOptions } from '@tanstack/react-query'
import { useQueryState } from 'nuqs'
import { useMemo } from 'react'
import { DataTableResetFilter } from '@/common/ui/table/data-table-reset-filter'
import { DataTableArrow } from '@/common/ui/table/data-table-arrow'
import BlacklistLegend from '@/common/components/BlacklistLegend'
import { SortOrder } from '@/schema'
import useQueryGetPaymentGateway from '@/modules/bank/api/hooks/useQueryGetPaymentGateway'
import { Combobox } from '@/common/ui/combobox'

interface WithdrawTableProps {
	data: WithdrawTransactionResponse['data']
	total: number
	onRefresh: (options?: RefetchOptions) => Promise<unknown>
	isLoading?: boolean
	onSort?: (columnId: string, order: SortOrder | undefined) => void
}

export default function WithdrawTable({
	data,
	total,
	onRefresh,
	isLoading,
	onSort,
}: WithdrawTableProps) {
	const [paymentGatewayId, setPaymentGatewayId] =
		useQueryState('paymentGatewayId')
	const [playerBankType, setPlayerBankType] = useQueryState('playerBankType')

	const { data: paymentGatewaysData } = useQueryGetPaymentGateway({
		skip: 0,
		take: 1000,
	})

	const paymentGatewayOptions = useMemo(
		() => [
			{ label: 'ทั้งหมด ', value: '' },
			...(paymentGatewaysData?.data
				.filter((pg) => pg.bankProviderName)
				.map((pg) => ({
					label: pg.bankProviderName,
					value: pg.id,
				})) || []),
		],
		[paymentGatewaysData],
	)

	return (
		<div className="space-y-4">
			{/* Filter Controls - Mobile Responsive */}
			<BlacklistLegend />
			<div className="space-y-4">
				{/* Filter Controls Row */}
				<div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
					<DataTableSearch
						placeHolder="ค้นหา..."
						searchHint="สามารถค้นหาด้วย: เบอร์โทร, เลขบัญชี, ชื่อบัญชีธนาคาร, รหัสธุรกรรม"
					/>

					<DataTableResetFilter />

					<Combobox
						options={paymentGatewayOptions}
						placeholder="เลือกบัญชีรับเงิน"
						value={paymentGatewayId ?? undefined}
						onValueChange={(val) => setPaymentGatewayId(val || null)}
					/>

					<Combobox
						options={playerBankTypeOptions}
						placeholder="เลือกประเภทบัญชีผู้เล่น"
						value={playerBankType ?? undefined}
						onValueChange={(val) => setPlayerBankType(val || null)}
					/>

					<DataTableProcessorSelector
						label="ประเภทการถอน"
						paramKey="processBy"
						options={processorOptions}
					/>

					<Button
						variant="outline"
						size="sm"
						onClick={() => onRefresh()}
						title="รีเฟรชข้อมูล"
						className="h-8 w-8 sm:h-9 sm:w-9"
					>
						<Icons.refresh className="h-4 w-4" />
					</Button>
				</div>
			</div>

			{/* Status Filter - Full Width */}
			<div className="w-full">
				<DataTableStatusSelector
					options={statusOptions}
					label="สถานะ"
					paramKey="status"
					defaultValues={defaultStatusValues}
				/>
			</div>

			{/* Data Table */}
			<DataTableArrow
				columns={columns}
				data={data}
				total={total}
				isLoading={isLoading}
				onSort={onSort}
				getRowClassName={(row) =>
					row.isBlacklist
						? 'bg-red-100 border-l-4 border-l-red-600 hover:bg-red-200 text-red-900 font-medium shadow-sm ring-1 ring-red-200'
						: ''
				}
			/>
		</div>
	)
}
